import { FC } from 'react';

import { GiftCouponForRedemption } from '@shared/api/giftCoupon/giftCoupon.interface';

import {
  formatDateTime,
  formatPhone,
  getChoiceUnitName,
  getItemName,
} from '../utils/redemption.utils';

interface Props {
  todayDelivered: GiftCouponForRedemption[];
  showTodayDelivered: boolean;
  setShowTodayDelivered: (fn: (prev: boolean) => boolean) => void;
  copyToClipboard: (text: string) => void;
}

export const TodayDeliveredTable: FC<Props> = ({
  todayDelivered,
  showTodayDelivered,
  setShowTodayDelivered,
  copyToClipboard,
}) => {
  if (todayDelivered.length === 0) return null;

  return (
    <div className="px-4 pb-2">
      <button
        onClick={() => setShowTodayDelivered((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-left transition-colors hover:bg-green-100"
      >
        <span className="text-sm font-medium text-green-800">
          오늘 발송 완료 ({todayDelivered.length}건)
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-green-600 transition-transform ${
            showTodayDelivered ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {showTodayDelivered && (
        <div className="mt-2 overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full">
            <thead className="bg-green-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  쿠폰번호
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  _id
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  상품
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  메모
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  확인번호
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  수신자
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-green-700">
                  도매가
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  신청시각
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                  발송시각
                </th>
              </tr>
            </thead>
            <tbody>
              {todayDelivered.map((coupon, idx) => {
                const curKey = formatDateTime(coupon.gifticon?.deliveredAt, true);
                const prevKey =
                  idx > 0
                    ? formatDateTime(todayDelivered[idx - 1]?.gifticon?.deliveredAt, true)
                    : null;
                const isNewGroup = idx === 0 || curKey !== prevKey;
                const choiceUnitName = getChoiceUnitName(coupon);

                return (
                  <tr
                    key={coupon._id}
                    className={`hover:bg-gray-50 ${
                      isNewGroup && idx > 0 ? 'border-t border-gray-300' : ''
                    }`}
                  >
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-700">
                      {coupon.fullCode || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <button
                        onClick={() => copyToClipboard(coupon._id)}
                        className="font-mono text-xs text-gray-400 hover:text-indigo-600"
                        title="클릭하여 _id 복사"
                      >
                        {coupon._id.slice(-6)}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-900">
                      {getItemName(coupon)}
                      {choiceUnitName && (
                        <span className="ml-1 text-indigo-500">({choiceUnitName})</span>
                      )}
                    </td>
                    <td className="max-w-[120px] px-4 py-2">
                      {coupon.memo ? (
                        <p className="truncate text-xs text-gray-600" title={coupon.memo}>
                          {coupon.memo}
                        </p>
                      ) : (
                        <span className="text-[10px] text-gray-300">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-amber-600">
                      {coupon.additionalRecipientPhone ? (
                        formatPhone(coupon.additionalRecipientPhone)
                      ) : (
                        <span className="text-[10px] text-gray-300">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-700">
                      {formatPhone(coupon.recipient?.phoneNumber)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-indigo-600">
                      {coupon.gifticon?.priceWhole != null
                        ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                      {formatDateTime(coupon.recipient?.requestedAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                      {formatDateTime(coupon.gifticon?.deliveredAt, true)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
